import React, { useMemo } from 'react';
import clsx from 'clsx';
import { CalendarPlus, Check, Repeat, MapPin } from 'lucide-react';
import { addDays, startOfDay, sameDay, ymd, fmtRange, isAllDayLike, weekdayName, shortMonth } from './calendarMath';
import { Button } from '../kit';

// Schedule view: the next 30 days as a list, a day per row group (Google's "Schedule").
export default function Agenda({ from, to, occs, colorFor, onSelect, onCreate, now, search }) {
  const today = now || new Date();
  const days = useMemo(() => {
    const out = [];
    for (let d = startOfDay(from); d <= to; d = addDays(d, 1)) {
      const next = addDays(d, 1);
      const list = occs.filter((o) => o.start < next && o.end > d);
      if (list.length) out.push({ d, list });
    }
    return out;
  }, [from, to, occs]);

  if (!days.length) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-center px-6 py-16 bg-white">
        <span className="w-14 h-14 rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-4"><CalendarPlus size={24} aria-hidden /></span>
        <h3 className="text-[16px] font-semibold text-slate-900">{search ? `Nothing matches “${search}”` : 'Nothing booked in these 30 days'}</h3>
        <p className="text-[13px] text-slate-500 mt-1 max-w-sm">
          {search ? 'Try another name, or clear the search.' : 'Book a meeting, visit or call, or show more calendars on the left.'}
        </p>
        {!search && <Button className="mt-4" icon={CalendarPlus} onClick={() => onCreate(null)}>Book an appointment</Button>}
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto bg-white sc-cal-scroll">
      <ol className="divide-y divide-slate-100">
        {days.map(({ d, list }) => {
          const isToday = sameDay(d, today);
          return (
            <li key={ymd(d)} className="flex gap-3 sm:gap-6 px-3 sm:px-5 py-3">
              <div className="w-10 sm:w-28 flex-shrink-0 flex items-start gap-2 pt-1">
                <span className={clsx('w-9 h-9 rounded-full flex items-center justify-center text-[18px] font-medium tabular-nums', isToday ? 'bg-indigo-600 text-white' : 'text-slate-800')}>{d.getDate()}</span>
                <span className={clsx('hidden sm:block text-[11px] font-semibold uppercase tracking-[0.06em] mt-2.5', isToday ? 'text-indigo-600' : 'text-slate-500')}>
                  {shortMonth(d.getMonth())}, {weekdayName(d.getDay()).slice(0, 3)}
                </span>
              </div>
              <ul className="flex-1 min-w-0 space-y-0.5">
                {list.map((o) => {
                  const c = colorFor(o);
                  const allDay = isAllDayLike(o);
                  return (
                    <li key={o.key}>
                      <button type="button" onClick={(e) => onSelect(o, e.currentTarget.getBoundingClientRect())}
                        className={clsx('w-full flex items-center gap-3 rounded-lg px-2.5 py-2 text-left hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500',
                          o.end < today && 'opacity-60')}>
                        <span className="w-2.5 h-2.5 rounded-full flex-shrink-0" style={{ background: c.hex }} aria-hidden />
                        <span className="hidden sm:block w-36 flex-shrink-0 text-[12.5px] text-slate-600 tabular-nums">{allDay ? 'All day' : fmtRange(o.start, o.end)}</span>
                        <span className="flex-1 min-w-0">
                          <span className={clsx('flex items-center gap-1.5 text-[13.5px] font-semibold text-slate-900', o.status === 'cancelled' && 'line-through text-slate-400')}>
                            {o.status === 'completed' && <Check size={13} className="text-emerald-600 flex-shrink-0" aria-label="Done" />}
                            <span className="truncate">{o.title}</span>
                            {o.recurring && <Repeat size={11} className="text-slate-400 flex-shrink-0" aria-label="Repeats" />}
                          </span>
                          <span className="block text-[12px] text-slate-500 truncate">
                            <span className="sm:hidden tabular-nums text-slate-600">{allDay ? 'All day' : fmtRange(o.start, o.end)}{(o.participant || o.coordinator_name || o.typeLabel) ? ' · ' : ''}</span>
                            {[o.participant, o.kind === 'keydate' ? o.typeLabel : o.coordinator_name].filter(Boolean).join(' · ')}
                            {o.location && <span className="inline-flex items-center gap-0.5 ml-1.5"><MapPin size={10} aria-hidden />{o.location}</span>}
                          </span>
                        </span>
                      </button>
                    </li>
                  );
                })}
              </ul>
            </li>
          );
        })}
      </ol>
    </div>
  );
}
